/**
 * ObligationDetailDrawer - Shared drawer component for viewing obligation details
 * Features: Overview, Checklist, Evidence, Comments, Audit Trail tabs
 * Implements Three Superpowers: Cross-Framework Intelligence, Ownership, Audit Trail
 */
import React, { useState, useEffect } from 'react';
import { 
  X, 
  CheckCircle2, 
  Clock, 
  User, 
  FileText, 
  MessageSquare, 
  Shield,
  Link as LinkIcon,
  Upload,
  Loader2,
  AlertTriangle
} from 'lucide-react';

import { apiFetch } from '../../services/api_service';

const PRIORITY_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  medium: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  low: 'bg-[#2F2F2F] text-[#6B6B6B] border-[#2F2F2F]',
};

const STATUS_COLORS = {
  completed: 'bg-emerald-500/20 text-emerald-400',
  in_progress: 'bg-blue-500/20 text-blue-400',
  pending: 'bg-amber-500/20 text-amber-400',
  blocked: 'bg-red-500/20 text-red-400',
  overdue: 'bg-red-500/20 text-red-400',
};

export default function ObligationDetailDrawer({ obligationId, open, onClose }) {
  const [obligation, setObligation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [completing, setCompleting] = useState(null);

  useEffect(() => {
    if (obligationId && open) {
      fetchObligation();
    }
  }, [obligationId, open]);

  const fetchObligation = async () => {
    setLoading(true);
    try {
      const data = await apiFetch(`/v1/obligations/${obligationId}`);
      if (data && !data._error) {
        setObligation(data);
      }
    } catch (err) {
      console.error('Error fetching obligation:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteChecklistItem = async (itemIndex) => {
    setCompleting(itemIndex);
    try {
      const data = await apiFetch(
        `/v1/obligations/${obligationId}/progress`,
        { method: 'PUT', body: JSON.stringify({ 
            current_step: itemIndex + 1,
            step_status: 'completed'
          }) }
      );
      if (data && !data._error) {
        fetchObligation();
      }
    } catch (err) {
      console.error('Error completing checklist item:', err);
    } finally {
      setCompleting(null);
    }
  };

  if (!open) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        data-testid="drawer-backdrop"
      />
      
      {/* Drawer */}
      <div 
        className="fixed inset-y-0 right-0 w-full max-w-2xl bg-[#0A0A0A] border-l border-[#1F1F1F] z-50 flex flex-col overflow-hidden"
        data-testid="obligation-detail-drawer"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : !obligation ? (
          <div className="flex flex-col items-center justify-center h-full">
            <AlertTriangle className="h-12 w-12 text-red-400 mb-4" />
            <p className="text-[#6B6B6B]">Obligation not found</p>
            <button onClick={onClose} className="mt-4 text-emerald-400 hover:underline">
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-[#1F1F1F] bg-[#111111] shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="px-2 py-0.5 text-xs font-mono bg-[#1F1F1F] text-[#A0A0A0] rounded">
                      {obligation.obligation_code}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded ${PRIORITY_COLORS[obligation.priority] || PRIORITY_COLORS.medium}`}>
                      {obligation.priority}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded capitalize ${STATUS_COLORS[obligation.status] || STATUS_COLORS.pending}`}>
                      {(obligation.status || 'pending').replace('_', ' ')}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-white">
                    {obligation.obligation_title}
                  </h2>
                </div>
                <button 
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-[#1F1F1F] text-[#6B6B6B] hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#A0A0A0]">Progress</span>
                  <span className="text-sm font-semibold text-white">
                    {Math.round(obligation.progress?.percent_complete || 0)}%
                  </span>
                </div>
                <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${obligation.progress?.percent_complete || 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-4 border-b border-[#1F1F1F] shrink-0">
              <div className="flex gap-1">
                {['overview', 'checklist', 'evidence', 'comments', 'audit'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg capitalize transition-colors ${
                      activeTab === tab 
                        ? 'bg-[#1F1F1F] text-white' 
                        : 'text-[#6B6B6B] hover:text-white'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-2">Description</h3>
                    <p className="text-sm text-[#A0A0A0]">{obligation.obligation_description || 'No description provided'}</p>
                  </div>

                  {/* Meta Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg">
                      <p className="text-xs text-[#6B6B6B] mb-1">Due Date</p>
                      <p className="text-sm font-medium text-white">
                        {formatDate(obligation.due_date)}
                      </p>
                      {obligation.due_date && (
                        <p className={`text-xs mt-1 ${
                          getDaysUntilDue(obligation.due_date) < 0 ? 'text-red-400' :
                          getDaysUntilDue(obligation.due_date) <= 7 ? 'text-amber-400' : 'text-[#6B6B6B]'
                        }`}>
                          {getDaysUntilDue(obligation.due_date) < 0 
                            ? `${Math.abs(getDaysUntilDue(obligation.due_date))} days overdue`
                            : `in ${getDaysUntilDue(obligation.due_date)} days`}
                        </p>
                      )}
                    </div>

                    <div className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg">
                      <p className="text-xs text-[#6B6B6B] mb-1">Owner</p>
                      <p className="text-sm font-medium text-white">
                        {obligation.assigned_to?.user_name || 'Unassigned'}
                      </p>
                      {obligation.assigned_to?.user_role && (
                        <p className="text-xs text-[#6B6B6B] mt-1">{obligation.assigned_to.user_role}</p>
                      )}
                    </div>

                    <div className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg">
                      <p className="text-xs text-[#6B6B6B] mb-1">Framework</p>
                      <span className="px-2 py-1 text-xs bg-[#1F1F1F] text-[#A0A0A0] rounded">
                        {(obligation.framework_code || 'General').replace('_', '/')}
                      </span>
                    </div>

                    <div className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg">
                      <p className="text-xs text-[#6B6B6B] mb-1">Category</p>
                      <p className="text-sm font-medium text-white capitalize">
                        {obligation.category || obligation.obligation_type?.replace(/_/g, ' ') || 'General'}
                      </p>
                    </div>
                  </div>

                  {/* Cross-Framework Intelligence (Superpower #1) */}
                  {obligation.cross_framework_equivalents?.length > 0 && (
                    <div className="p-4 rounded-lg bg-teal-500/10 border border-teal-500/30">
                      <h3 className="text-sm font-semibold text-teal-400 mb-2 flex items-center gap-2">
                        <LinkIcon size={16} />
                        Cross-Framework Intelligence
                      </h3>
                      <p className="text-sm text-teal-300 mb-2">
                        This obligation also satisfies requirements in:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {obligation.cross_framework_equivalents.map((eq, idx) => (
                          <span key={idx} className="px-2 py-1 text-xs bg-teal-500/20 text-teal-400 rounded">
                            {eq.frameworkCode} {eq.requirementCode}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'checklist' && (
                <div className="space-y-3">
                  {(obligation.progress?.steps || []).map((step, idx) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border ${
                        step.status === 'completed' 
                          ? 'bg-emerald-500/10 border-emerald-500/30'
                          : 'bg-[#111111] border-[#1F1F1F]'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={() => step.status !== 'completed' && handleCompleteChecklistItem(idx)}
                          disabled={step.status === 'completed' || completing === idx}
                          className="mt-0.5"
                        >
                          {completing === idx ? (
                            <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                          ) : step.status === 'completed' ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                          ) : (
                            <div className="h-5 w-5 rounded-full border-2 border-[#4A4A4A] hover:border-emerald-500 transition-colors" />
                          )}
                        </button>

                        <div className="flex-1">
                          <p className={`text-sm font-medium ${
                            step.status === 'completed' ? 'text-emerald-400' : 'text-white'
                          }`}>
                            {step.step_name}
                          </p>
                          {step.completed_at && (
                            <p className="text-xs text-[#6B6B6B] mt-1">
                              Completed {formatDate(step.completed_at)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {(!obligation.progress?.steps || obligation.progress.steps.length === 0) && (
                    <div className="text-center py-8">
                      <p className="text-[#6B6B6B]">No checklist items defined</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'evidence' && (
                <div className="space-y-4">
                  {(obligation.evidence_requirements?.required_evidence_types || []).map((type, idx) => (
                    <div key={idx} className="p-4 rounded-lg border border-[#1F1F1F] bg-[#111111]">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white mb-1 capitalize">
                            {type.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-[#6B6B6B]">
                            Required evidence type
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded ${
                          (obligation.evidence_requirements?.current_evidence_count || 0) > 0
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {(obligation.evidence_requirements?.current_evidence_count || 0) > 0 ? 'Uploaded' : 'Missing'}
                        </span>
                      </div>

                      <button className="w-full py-2 px-4 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                        <Upload size={16} />
                        Upload Evidence
                      </button>
                    </div>
                  ))}
                  
                  {(!obligation.evidence_requirements?.required_evidence_types || 
                    obligation.evidence_requirements.required_evidence_types.length === 0) && (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 text-[#2F2F2F] mx-auto mb-4" />
                      <p className="text-[#6B6B6B]">No evidence requirements specified</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'comments' && (
                <div className="space-y-4">
                  {(obligation.comments || []).map((comment, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-[#111111] border border-[#1F1F1F]">
                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-[#1F1F1F] flex items-center justify-center shrink-0">
                          <User size={14} className="text-[#6B6B6B]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-white">
                              {comment.user_name || 'Unknown User'}
                            </p>
                            <p className="text-xs text-[#6B6B6B]">
                              {formatDate(comment.created_at)}
                            </p>
                          </div>
                          <p className="text-sm text-[#A0A0A0]">{comment.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {(!obligation.comments || obligation.comments.length === 0) && (
                    <div className="text-center py-8">
                      <MessageSquare className="h-12 w-12 text-[#2F2F2F] mx-auto mb-4" />
                      <p className="text-[#6B6B6B]">No comments yet</p>
                    </div>
                  )}

                  {/* Add Comment */}
                  <div className="mt-4 pt-4 border-t border-[#1F1F1F]">
                    <textarea
                      placeholder="Add a comment..."
                      className="w-full px-4 py-3 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none resize-none"
                      rows={3}
                    />
                    <button className="mt-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 text-sm font-medium rounded-lg transition-colors">
                      Post Comment
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'audit' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield size={20} className="text-purple-400" />
                      <h3 className="text-sm font-semibold text-purple-400">
                        Immutable Audit Trail
                      </h3>
                    </div>
                    <p className="text-sm text-purple-300">
                      Complete audit trail available for this obligation showing all actions,
                      evidence uploads, and status changes.
                    </p>
                    <button className="mt-3 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 text-purple-400 text-sm font-medium rounded-lg transition-colors">
                      View Complete Audit Trail
                    </button>
                  </div>

                  {/* Status History */}
                  <div>
                    <h4 className="text-sm font-medium text-white mb-3">Status History</h4>
                    <div className="space-y-2">
                      {(obligation.status_history || []).slice().reverse().map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg">
                          <div className={`h-2 w-2 rounded-full ${
                            entry.status === 'completed' ? 'bg-emerald-500' :
                            entry.status === 'overdue' ? 'bg-red-500' :
                            entry.status === 'in_progress' ? 'bg-blue-500' : 'bg-amber-500'
                          }`} />
                          <span className="text-sm text-white capitalize">{entry.status?.replace('_', ' ')}</span>
                          <span className="text-xs text-[#6B6B6B] ml-auto">
                            {formatDate(entry.timestamp)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
